import { html } from 'lit-html';
import { get } from '../api.js';
import { emptyRow, helpLink, type Page } from '../app.js';
import { ago, statusPill, when } from '../format.js';
import { agentHref } from './reports.js';

export interface Health {
  status: 'ok' | 'degraded' | 'down';
  settings: {
    loaded: boolean;
    workerMode: 'capture' | 'detect' | 'enforce';
    dryRun: boolean;
    allowLiveEnforcement: boolean;
    queuesEnforce: number;
    queuesDryRun: number;
    exemptions: number;
  };
  database: { reachable: boolean; migrationVersion: number };
  genesys: { configured: boolean; lastSuccessAt: string | null; lastError: string | null; lastErrorAt: string | null };
  eventSource: { kind: string; state: string; error: string | null; lastEventReceivedAt: string | null; dlqDepth: number | null };
  processing: { pendingRecords: number | null; failedLast24h: number | null; lastTimeoutProcessedAt: string | null };
}

interface Row {
  id: string;
  conversation_id: string;
  agent_id: string;
  agent_name: string | null;
  queue_id: string;
  queue_name: string | null;
  status: string;
  genesys_event_time: string;
  inactive_from?: string | null;
}

interface Summary {
  counts: { timeouts_24h: number; timeouts_7d: number; deactivations_7d: number; open_now: number; failed_24h: number; pending: number };
  open: Row[];
  recent: Row[];
}

let summary: Summary | null = null;
export let health: Health | null = null;

export async function loadHealth(): Promise<Health> {
  health = await get<Health>('/health');
  return health;
}

export function modeBanner(h: Health | null) {
  if (!h?.settings.loaded) return null;
  const s = h.settings;
  if (s.workerMode !== 'enforce') {
    return html`<div class="banner info"><strong>Observe only.</strong> Worker mode is <b>${s.workerMode}</b>: timeouts are ${s.workerMode === 'capture' ? 'captured as raw events' : 'recorded'}, and nobody is deactivated.</div>`;
  }
  if (s.dryRun) {
    return html`<div class="banner info"><strong>Dry run.</strong> Every decision is recorded as "would deactivate", and nobody is deactivated.</div>`;
  }
  if (!s.allowLiveEnforcement) {
    return html`<div class="banner warning"><strong>Live enforcement is switched off in the infrastructure.</strong> Everything runs as a dry run until the deployment allows it.</div>`;
  }
  return html`<div class="banner live"><strong>Enforcing live</strong> on ${s.queuesEnforce} queue${s.queuesEnforce === 1 ? '' : 's'}${s.queuesDryRun ? html`, dry run on ${s.queuesDryRun} more` : ''}.</div>`;
}

const who = (r: Row) => r.agent_name ?? r.agent_id;
const queue = (r: Row) => r.queue_name ?? r.queue_id;

export const dashboard: Page = {
  title: 'Dashboard',
  async load() {
    const [s] = await Promise.all([get<Summary>('/summary'), loadHealth()]);
    summary = s;
  },
  view() {
    const h = health;
    const c = summary?.counts;
    const genesys = !h
      ? null
      : !h.genesys.configured
        ? html`<span class="pill warning">Not configured</span><p class="muted">${h.genesys.lastError ?? "Genesys credentials aren't set yet."}</p>`
        : h.genesys.lastSuccessAt
          ? html`<span class="pill positive">Connected</span><p class="muted">Signed in ${ago(h.genesys.lastSuccessAt)}</p>`
          : html`<span class="pill critical">Not signed in</span><p class="muted">${h.genesys.lastError ?? 'Waiting for first sign-in'}</p>`;
    return html`
      <h1>Dashboard ${helpLink('dashboard')}</h1>
      ${modeBanner(h)}
      <div class="grid health">
        <div class="card">
          <span class="label">Worker</span>
          ${h ? html`<span class="pill ${h.status === 'ok' ? 'positive' : h.status === 'degraded' ? 'warning' : 'critical'}">${h.status === 'ok' ? 'Healthy' : h.status}</span>
            <p class="muted">Database ${h.database.reachable ? 'reachable' : 'unreachable'}</p>` : '—'}
        </div>
        <div class="card"><span class="label">Genesys</span>${genesys ?? '—'}</div>
        <div class="card">
          <span class="label">Event feed</span>
          ${h ? html`<span class="pill ${h.eventSource.state === 'receiving' ? 'positive' : h.eventSource.state === 'disabled' ? 'neutral' : 'warning'}">${h.eventSource.state}</span>
            <p class="muted">${h.eventSource.lastEventReceivedAt ? `Last Genesys event ${ago(h.eventSource.lastEventReceivedAt)}` : 'No Genesys events received yet'}</p>` : '—'}
        </div>
        <div class="card">
          <span class="label">Failed messages</span>
          ${h ? h.eventSource.dlqDepth ? html`<span class="pill critical">${h.eventSource.dlqDepth} in dead-letter queue</span>` : html`<span class="pill positive">None</span>` : '—'}
          <p class="muted">${c ? `${c.pending} timeout${c.pending === 1 ? '' : 's'} being processed` : ''}</p>
        </div>
      </div>

      <div class="grid kpis">
        <div class="kpi"><span class="label">Timeouts, 24 h</span><span class="value">${c?.timeouts_24h ?? '—'}</span></div>
        <div class="kpi"><span class="label">Timeouts, 7 days</span><span class="value">${c?.timeouts_7d ?? '—'}</span></div>
        <div class="kpi"><span class="label">Deactivations, 7 days</span><span class="value">${c?.deactivations_7d ?? '—'}</span></div>
        <div class="kpi"><span class="label">Deactivated right now</span><span class="value">${c?.open_now ?? '—'}</span></div>
        <div class="kpi ${c?.failed_24h ? 'bad' : ''}"><span class="label">Failed, 24 h</span><span class="value">${c?.failed_24h ?? '—'}</span></div>
      </div>

      <section>
        <h2>Deactivated right now</h2>
        <p class="hint">These agents timed out and haven't reactivated the queue yet. They reactivate it themselves in Genesys.</p>
        <div class="table-wrap"><table>
          <thead><tr><th>Agent</th><th>Queue</th><th>Inactive since</th><th>Status</th></tr></thead>
          <tbody>${summary?.open.length
            ? summary.open.map((r) => html`<tr><td>${who(r)}</td><td>${queue(r)}</td><td>${when(r.inactive_from)} <span class="muted">(${ago(r.inactive_from)})</span></td><td>${statusPill(r.status)}</td></tr>`)
            : emptyRow(4, 'Nobody is deactivated right now.')}</tbody>
        </table></div>
      </section>

      <section>
        <h2>Latest timeouts</h2>
        <div class="table-wrap"><table>
          <thead><tr><th>When</th><th>Agent</th><th>Queue</th><th>Outcome</th></tr></thead>
          <tbody>${summary?.recent.length
            ? summary.recent.map((r) => html`<tr><td class="nowrap">${when(r.genesys_event_time)}</td><td><a href=${agentHref(r.agent_id)}>${who(r)}</a></td><td>${queue(r)}</td><td>${statusPill(r.status)}</td></tr>`)
            : emptyRow(4, 'No timeouts recorded yet.')}</tbody>
        </table></div>
        <p><a href="#/timeouts">All timeouts →</a></p>
      </section>`;
  },
};
